import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import NakshatraAnalysis from "./NakshatraAnalysis";
import { ELEMENT_COLORS, getChartSlices } from "./NakshatraColorMapping";

interface CustomProps {
  data: any;
  planets: any[];
}

const ElementPada = (props: CustomProps) => {
  const { t } = useTranslation();
  const [data1, setData1] = useState<any[]>([]);
  const [data2, setData2] = useState<any[]>([]);
  const [data3, setData3] = useState<any[]>([]);
  const [data4, setData4] = useState<any[]>([]);
  const [header, setHeader] = useState<any[]>([]);

  const tableHeader2 = [
    {
      name: `${t("elementTemperamentPadaTitle")}`,
      field: "key",
    },
    {
      name: `${t("percentageElementTemperamentPadaTitle")}`,
      field: "value",
    },
  ];

  const tableHeader3 = [
    {
      name: `${t("elementTemperamentPadaTitle")}`,
      field: "key",
    },
    {
      name: `${t("countElementTemperamentPadaTitle")}`,
      field: "value",
    },
  ];

  useEffect(() => {
    convertDataToTableFormat(props.data);
  }, [props.data, props.planets]);

  interface TemperamentValues {
    [key: string]: number;
  }

  const calculateValues = (
    temperamentNames: string[],
    nakshatraTemperament: string,
    padaTemperament: string
  ): TemperamentValues => {
    const values: TemperamentValues = {};

    temperamentNames.forEach((temperament) => {
      values[temperament.toLowerCase()] = 0;
    });

    if (
      temperamentNames.every((t) =>
        nakshatraTemperament.toLowerCase().includes(t)
      )
    ) {
      temperamentNames.forEach((temperament) => {
        values[temperament.toLowerCase()] = 100;
      });
    } else {
      temperamentNames.forEach((temperament) => {
        if (
          nakshatraTemperament.toLowerCase().includes(temperament.toLowerCase())
        ) {
          values[temperament.toLowerCase()] += 75;
        }
        if (padaTemperament.toLowerCase().includes(temperament.toLowerCase())) {
          values[temperament.toLowerCase()] += 25;
        }
      });
    }

    return values;
  };

  const convertDataToTableFormat = (data: any) => {
    const temperamentNames: string[] = Array.from(
      new Set(
        data.flatMap(
          (item: { nakshatra_element: string; pada_element: string }) => [
            capitalizeFirstLetter(item.nakshatra_element),
            capitalizeFirstLetter(item.pada_element),
          ]
        )
      )
    );

    const dynamicColumns = temperamentNames.map((temperamentName: string) => ({
      name: temperamentName,
      field: temperamentName.toLowerCase(),
    }));

    // Create the base header array fresh each time to avoid duplicates
    const baseHeader = [
      {
        name: `${t("planetsTitle")}`,
        field: "point_name",
      },
      {
        name: `${t("nakshatraTitle")}`,
        field: "nakshatra",
      },
      {
        name: `${t("nakshatraElementTitle")}`,
        field: "nakshatra_element",
      },
      {
        name: `${t("padaTitle")}`,
        field: "pada",
      },
      {
        name: `${t("padaSignTitle")}`,
        field: "pada_sign",
      },
      {
        name: `${t("padaElementTitle")}`,
        field: "pada_element",
      },
    ];

    setHeader([...baseHeader, ...dynamicColumns]);

    const tableValue = data
      .filter((item: any) => props.planets.includes(item.point_name))
      .map((item: any) => {
        const temperamentValues: TemperamentValues = calculateValues(
          temperamentNames,
          item.nakshatra_element,
          item.pada_element
        );

        Object.keys(temperamentValues).forEach((key) => {
          if (temperamentValues[key] === 0) {
            delete temperamentValues[key];
          }
        });

        return {
          point_name: item.point_name,
          nakshatra: `${item.nakshatra_key}.${item.name}`,
          nakshatra_element: capitalizeFirstLetter(item.nakshatra_element),
          pada: item.pada,
          pada_sign: item.pada_sign,
          pada_element: capitalizeFirstLetter(item.pada_element),
          ...temperamentValues,
        };
      });

    setData1(tableValue);
    percentageCalculation(tableValue, temperamentNames);
  };

  const percentageCalculation = (data: any, temperamentNames: string[]) => {
    const temperamentCounts: any = {};

    data.forEach((item: any) => {
      temperamentNames.forEach((temperament) => {
        if (temperamentCounts[temperament] === undefined) {
          temperamentCounts[temperament] = 0;
        }
        if (item[temperament.toLowerCase()] !== undefined) {
          temperamentCounts[temperament] += item[temperament.toLowerCase()];
        }
      });
    });

    const totalCount = data.length * 100;

    const percentages: any = {};

    Object.keys(temperamentCounts).forEach((temperament) => {
      if (temperamentCounts[temperament] !== 0) {
        percentages[temperament] =
          (temperamentCounts[temperament] / totalCount) * 100;
      }
    });

    const temperamentPercentageData = Object.keys(percentages).map(
      (temperament) => ({
        key: temperament,
        value: `${percentages[temperament].toFixed(2)} %`,
        percentage: percentages[temperament].toFixed(2),
      })
    );

    setData2(temperamentPercentageData);
    setData3(getMaxObjects(temperamentPercentageData));

    const formattedData = [
      ["key", "value"],
      ...temperamentPercentageData.map((item) => [
        item.key,
        parseFloat(item.percentage),
      ]),
    ];
    setData4(formattedData);
  };

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getMaxObjects = (data: any[]) => {
    const max = Math.max(...data.map((item) => +item.percentage));
    return data.filter((item) => +item.percentage === max);
  };

  const getElementPada = () => {
    return (
      <NakshatraAnalysis
        chartTitle={"elementTemperamentPadaTitle"}
        chartData={data4}
        chartSlices={getChartSlices(data4, ELEMENT_COLORS)}
        tableHeader1={header}
        tableData1={data1}
        tableHeader2={tableHeader2}
        tableData2={data2}
        tableHeader3={tableHeader3}
        tableData3={data3}
      />
    );
  };

  return getElementPada();
};

export default ElementPada;
